import { CountCard } from "../../../../components/CountCard/CountCard";
import Empty from "../../../../components/EmptyPagination/Empty";
import usePagination from "src/hooks/usePagination";
import { JobApplication, JobApplicationStatus, Talent } from "src/types";
import PaginatedDiv from "src/components/PaginatedDiv";
import Table from "src/components/Tables/Table";
import Button from "src/components/Button";
import { InterviewMetrics } from "src/network/interview/type";
import { useState } from "react";
import useSlugParam from "src/hooks/useSlug";
import LoaderPage from "src/components/Loader/LoaderPage";
import { getJobsApplicationsPaginated } from "src/network/job_application";
import { dateFormat1 } from "src/utils/dates/formats";
import { useNavigate } from "react-router-dom";
import { interviewInvite } from "src/network/interview";
import { customToast } from "src/utils/toast";
import { UserAvatar } from "src/components/Avatar";
import { PaginatedJobApplicationParams } from "src/network/job_application/type";
import DeclineApplicationModal from "../components/DeclineApplicationModal";
import Tabs from "src/components/Tabs";
import { useAppSelector } from "src/redux/store";
import ConstraintModal from "src/components/Modal/ConstraintModal";

type tab = "New Applicants" | "Rejected Applicants";
const tabs: tab[] = ["New Applicants", "Rejected Applicants"];
const tabStatus: { [key in tab]: JobApplicationStatus } = {
  "New Applicants": "APPLIED",
  "Rejected Applicants": "DECLINED"
};

const AppliedTalents = ({ metrics }: { metrics?: InterviewMetrics }) => {
  const navigate = useNavigate();
  const intMonthLimit = useAppSelector(
    (r) => !!r.client?.client?.Company.UsageConstraints?.completed_monthly_job_interviews_exceeded
  );
  const [inviteLimit, setInviteLimit] = useState(false);
  const { id } = useSlugParam();
  const [filters, setFilters] = useState<PaginatedJobApplicationParams>({
    job_id: id,
    status: "APPLIED"
  });
  const { data, count, fetching, fetch, refresh, removeData } = usePagination<JobApplication>({
    fetchPage: getJobsApplicationsPaginated,
    page_size: 10,
    filters
  });
  // Reminder email handling
  const [inviting, setInviting] = useState<number>();
  const invite = async (t: Talent) => {
    if (intMonthLimit) {
      setInviteLimit(true);
      return;
    }
    if (!id) return;
    setInviting(t.freelancer_id);
    try {
      const interview = await interviewInvite({
        job_id: id,
        data: {
          first_name: t.User.first_name || "",
          last_name: t.User.last_name || "",
          email: t.User.email_id
        }
      });
      if (!interview) throw new Error("No invited happened");
      const app = data?.find((a) => a.freelancer_id === t.freelancer_id);
      if (app) app.JobInterview = interview;
      refresh(); // To reload the changed app
    } catch (e) {
      console.error(e);
      customToast({ message: "Error occured sending invite" });
    }
    setInviting(undefined);
  };

  // Decliner handler
  const [declining, setDeclining] = useState<number>();
  const decline = () => {
    removeData((a) => a.id !== declining);
    setDeclining(undefined);
  };

  return (
    <>
      <div className="flex gap-16 mb-6">
        <CountCard color="green" title="Interviewed" count={metrics?.COMPLETED} />
        <CountCard color="yellow" title="Invited" count={metrics?.INVITED} />
        <CountCard color="green" title="Shorlisted" count={metrics?.PASSED} />
      </div>

      <Tabs
        style={1}
        className="mb-6"
        tabs={tabs}
        onChange={(s) => setFilters({ ...filters, status: tabStatus[s] })}
      />

      {count === 0 && (
        <Empty
          title="There are no applicants yet"
          text="It will be available once talent start applying"
        />
      )}

      {!data && <LoaderPage />}

      {data && !!count && (
        <PaginatedDiv
          className="!h-auto flex-1 "
          onLimit={fetch}
          loading={fetching}
          count={count}
          currentCount={data?.length}
        >
          <Table
            data={data}
            className="overflow-x-scroll"
            heads={[
              "",
              "Name | Email Address",
              "Role",
              "Salary",
              "Location",
              "Date Applied",
              "Action"
            ]}
            row={(a) => {
              const { Freelancer, createdAt, JobInterview, status } = a as JobApplication;
              const declined = status === "DECLINED";
              const invited = !!JobInterview;
              const { User, hourly_rate, city, Role, freelancer_id } = Freelancer;
              const { full_name, email_id } = User;
              return [
                <UserAvatar wrapperClassName={`!w-[48px] !h-[48px] `} user={User} />,
                <div>
                  <p>{full_name}</p>
                  <p className="max-w-[207px] overflow-hidden text-ellipsis mt-2">{email_id}</p>
                </div>,
                Role?.name || "-",
                hourly_rate ? `$${hourly_rate}/hr` : "-",
                city,
                dateFormat1(createdAt),
                <div className="flex gap-4">
                  <Button
                    onClick={() => navigate("/talent/" + freelancer_id)}
                    className="!text-sm !rounded-3xl !w-fit !px-4"
                  >
                    View
                  </Button>
                  {!declined && (
                    <Button
                      onClick={() => invite(Freelancer)}
                      loading={inviting === freelancer_id}
                      variant="white_blue"
                      className="!text-sm !rounded-3xl !w-fit !px-4"
                      disabled={invited || !!inviting}
                    >
                      {invited ? "Invited" : "Invite"}
                    </Button>
                  )}
                  {!invited && !declined && (
                    <Button
                      disabled={!!inviting}
                      onClick={() => setDeclining(a.id)}
                      className="!text-sm !rounded-3xl !w-fit !px-4"
                      variant="white_red"
                    >
                      Decline
                    </Button>
                  )}
                </div>
              ];
            }}
          />
        </PaginatedDiv>
      )}

      {/* Modals */}
      <DeclineApplicationModal
        id={declining}
        close={() => setDeclining(undefined)}
        onDecline={decline}
      />

      <ConstraintModal
        open={inviteLimit}
        variant="month_int_limit"
        onClose={() => setInviteLimit(false)}
      />
    </>
  );
};

export default AppliedTalents;
